const IDEMPOTENCY_KEY_SAFE_SCOPE = /[^A-Za-z0-9._:-]+/g;
const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;
// Expire before the server's 24-hour replay record so a recovered client key
// can never outlive the authoritative idempotency result.
const PERSISTED_INTENT_TTL_MS = 23 * 60 * 60 * 1000;
const MAX_PERSISTED_INTENTS_PER_SCOPE = 32;
const PERSISTED_INTENT_KEY_SEGMENT = '.intent.';

const stableSerialize = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => (
      `${JSON.stringify(key)}:${stableSerialize(value[key])}`
    )).join(',')}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
};

const createEntropy = () => {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

const digestPayloadSignature = (payloadSignature) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < payloadSignature.length; index += 1) {
    hash ^= payloadSignature.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const persistedIntentRecordKey = (storageKey, payloadSignature) => (
  `${storageKey}${PERSISTED_INTENT_KEY_SEGMENT}${digestPayloadSignature(payloadSignature)}`
);

const listPersistedIntentRecordKeys = (storageKey) => {
  if (!storageKey || typeof window === 'undefined') return [];
  const prefix = `${storageKey}${PERSISTED_INTENT_KEY_SEGMENT}`;
  const keys = [];
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
  } catch (_error) {
    return [];
  }
  return keys;
};

const prunePersistedIntents = (storageKey) => {
  const now = Date.now();
  const validRecords = [];
  for (const recordKey of listPersistedIntentRecordKeys(storageKey)) {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(recordKey));
      const ageMs = now - Number(parsed?.createdAt);
      if (
        !IDEMPOTENCY_KEY_PATTERN.test(String(parsed?.key || ''))
        || !Number.isFinite(ageMs)
        || ageMs < 0
        || ageMs >= PERSISTED_INTENT_TTL_MS
      ) {
        window.localStorage.removeItem(recordKey);
      } else {
        validRecords.push({ recordKey, createdAt: Number(parsed.createdAt) });
      }
    } catch (_error) {
      window.localStorage.removeItem(recordKey);
    }
  }
  validRecords
    .sort((left, right) => right.createdAt - left.createdAt)
    .slice(MAX_PERSISTED_INTENTS_PER_SCOPE)
    .forEach(({ recordKey }) => window.localStorage.removeItem(recordKey));
};

const readPersistedIntent = (storageKey, payloadSignature) => {
  if (!storageKey || typeof window === 'undefined') return null;
  const recordKey = persistedIntentRecordKey(storageKey, payloadSignature);
  try {
    prunePersistedIntents(storageKey);
    const raw = window.localStorage?.getItem(recordKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ageMs = Date.now() - Number(parsed?.createdAt);
    if (
      !IDEMPOTENCY_KEY_PATTERN.test(String(parsed?.key || ''))
      || parsed?.payloadDigest !== digestPayloadSignature(payloadSignature)
      || !Number.isFinite(ageMs)
      || ageMs < 0
      || ageMs >= PERSISTED_INTENT_TTL_MS
    ) {
      window.localStorage?.removeItem(recordKey);
      return null;
    }
    return { key: parsed.key, payloadSignature };
  } catch (_error) {
    return null;
  }
};

const persistIntent = (storageKey, intent) => {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    const recordKey = persistedIntentRecordKey(storageKey, intent.payloadSignature);
    window.localStorage?.setItem(recordKey, JSON.stringify({
      key: intent.key,
      payloadDigest: digestPayloadSignature(intent.payloadSignature),
      createdAt: Date.now(),
    }));
    prunePersistedIntents(storageKey);
  } catch (_error) {
    // An in-memory intent still protects retries in the current render session.
  }
};

const removePersistedIntent = (storageKey, payloadSignature) => {
  if (!storageKey || !payloadSignature || typeof window === 'undefined') return;
  try {
    window.localStorage?.removeItem(persistedIntentRecordKey(storageKey, payloadSignature));
  } catch (_error) {
    // Best effort only; payload signatures prevent stale intent reuse.
  }
};

export const createMutationIdempotencyKey = (scope = 'mutation') => {
  const safeScope = String(scope || 'mutation')
    .replace(IDEMPOTENCY_KEY_SAFE_SCOPE, '-')
    .slice(0, 48) || 'mutation';
  return `vylo.${safeScope}.${createEntropy()}`.slice(0, 128);
};

export const buildMutationIntentStorageKey = ({ operation, actorUserId, householdId } = {}) => {
  const safeOperation = String(operation || '')
    .replace(IDEMPOTENCY_KEY_SAFE_SCOPE, '-')
    .slice(0, 48);
  if (!safeOperation || actorUserId == null || actorUserId === '' || householdId == null || householdId === '') {
    return null;
  }
  return `vylo.mutation.${safeOperation}.actor-${String(actorUserId)}.household-${String(householdId)}`;
};

export const getOrCreateMutationIntent = (intentRef, { scope, payload, storageKey = null }) => {
  const payloadSignature = stableSerialize(payload);
  if (intentRef.current?.payloadSignature === payloadSignature) {
    return intentRef.current;
  }
  // A changed draft is a different intent, not proof that the previous request
  // failed. Keep every ambiguous payload record until its definitive response
  // or TTL so edit-away/edit-back and close/reopen recover the original key.
  const recovered = readPersistedIntent(storageKey, payloadSignature);
  if (recovered) {
    intentRef.current = recovered;
    return recovered;
  }
  const intent = {
    key: createMutationIdempotencyKey(scope),
    payloadSignature,
  };
  intentRef.current = intent;
  persistIntent(storageKey, intent);
  return intent;
};

export const clearMutationIntent = (intentRef, storageKey = null) => {
  const payloadSignature = intentRef.current?.payloadSignature || null;
  intentRef.current = null;
  removePersistedIntent(storageKey, payloadSignature);
};

export const createMutationContractError = ({ response, data, fallbackMessage }) => {
  const error = new Error(data?.error || fallbackMessage || 'Request failed');
  error.status = response?.status;
  error.code = data?.code || null;
  error.current = data?.current || null;
  error.retryable = data?.retryable === true;
  error.data = data || {};
  return error;
};

export const isVersionConflict = (value) => (
  value?.code === 'VERSION_CONFLICT'
  || value?.data?.code === 'VERSION_CONFLICT'
);

export const isIdempotencyContractError = (value) => (
  value?.code === 'IDEMPOTENCY_KEY_REUSED'
  || value?.code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS'
  || value?.data?.code === 'IDEMPOTENCY_KEY_REUSED'
  || value?.data?.code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS'
);

export const shouldRetainMutationIntent = (value) => {
  const code = value?.code || value?.data?.code || null;
  if (code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS') return true;
  const status = Number(value?.status);
  return !Number.isInteger(status) || status >= 500;
};

export { stableSerialize as stableMutationPayload };
